/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 목록 블록을 정의한다.
 *
 * @file /src/rootin/blocks/list/List.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 9.
 */
import { Block } from '@rootin/base/Block';
import { BlockClass } from '@rootin/base/Blocks';
import { Element } from '@rootin/base/Element';
import { Editor } from '@rootin/core/Editor';
import { Item } from './blocks/Item';
import { Container } from '@rootin/elements/container/Container';
import { Language, LanguageText } from '@utils/Language';
import './List.css';

export class List extends Block {
    /**
     * 블록타입
     */
    static type = 'list';

    /**
     * 블록종류
     */
    static kind: 'inline' | 'atomic' = 'atomic';

    /**
     * 블록아이콘
     */
    static icon: string =
        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-list-icon lucide-list"><path d="M3 12h.01"/><path d="M3 18h.01"/><path d="M3 6h.01"/><path d="M8 12h13"/><path d="M8 18h13"/><path d="M8 6h13"/></svg>';

    /**
     * 블록명
     */
    static label: LanguageText = Language.get('rootin.blocks.list.label');

    /**
     * 아이템 컨테이너
     */
    items: Container;

    /**
     * 목록 블록을 정의한다.
     *
     * @param editor - 이 블록이 속한 에디터
     */
    constructor(editor: Editor) {
        super(editor);
        this.items = new Container(this, {
            name: 'items',
            blocks: [Item.type],
            insertTarget: false,
            defaultBlock: () => new Item(this.editor),
        });
        this.$getBlock().append(this.items.$getElement());
    }

    /**
     * 타입 문자열로 블록 클래스를 가져온다. 아이템 블록타입은 이 블록만 알고 있으므로 직접
     * 처리하고, 그 외에는 상위 구현에 위임한다.
     *
     * @param type - 블록타입
     * @returns 해당 블록 클래스
     */
    getBlockClass(type: string): BlockClass | null {
        return type === Item.type ? (Item as BlockClass) : super.getBlockClass(type);
    }

    /**
     * 블록을 렌더링한다.
     */
    async render(): Promise<void> {
        await this.items.render();
    }

    /**
     * 블록이 비었는지 확인한다.
     *
     * @returns 블록이 비었는지 여부
     */
    isEmpty(): boolean {
        return this.items.isEmpty();
    }

    /**
     * 블록값을 가져온다.
     * element 변수가 존재할 경우, 해당 엘리먼트의 기본값을 가져온다.
     *
     * @param element - 기본값을 가져오려는 엘리먼트 (NULL 인 경우 전체 블록데이터)
     * @returns 블록값
     */
    getValue(element: Element = null): any {
        if (element !== null) {
            return this.defaultValue?.[element.name] ?? null;
        }

        return { id: this.getId(), type: this.getType(), items: this.items.getValue() };
    }

    /**
     * 블록값을 지정한다.
     *
     * @param value - 블록값
     * @param is_default_value - 기본값 여부
     */
    async setValue(value: { [key: string]: any }, is_default_value: boolean = false): Promise<void> {
        if (is_default_value === true) {
            this.defaultValue = value;
        }

        await this.items.setValue(value.items);
    }

    /**
     * 블록의 HTML 을 받아, 블록값을 지정한다.
     *
     * @param html - 블록 HTML
     */
    async setValueFromHtml(html: string): Promise<void> {
        const dom = document.createElement('div');
        dom.innerHTML = html;

        const $rootinBlock = dom.firstElementChild;
        if (
            $rootinBlock === null ||
            $rootinBlock.tagName !== 'ROOTIN-BLOCK' ||
            $rootinBlock.getAttribute('data-type') !== this.getType()
        ) {
            return;
        }

        const $items = Array.from($rootinBlock.children).find(
            (el) => el.tagName === 'ROOTIN-ELEMENT' && el.getAttribute('data-type') === 'container'
        );
        if ($items !== undefined) {
            await this.items.setValueFromHtml($items);
        }
    }

    /**
     * 현재 블록내 선택영역이 존재할 경우, 선택영역을 텍스트로 추출하고 블록에서 제거한다.
     *
     * @param is_all - 선택영역 부분추출이 아닌 전체를 추출할지 여부
     * @returns 추출한 텍스트 목록과, 선택 이후 남은영역이 포함된 블록(없는 경우 NULL)
     */
    async extractSelection(is_all: boolean): Promise<{ texts: string[]; remain: Block | null }> {
        const range = this.editor.selection.getRange();
        if (range === null || range.collapsed === true) {
            return { texts: [], remain: null };
        }

        // 드래그로 블록 전체가 잠긴(통째로 선택된) 상태라면, 아이템별 판단 없이 전체를 넘긴다.
        const isLocked = this.$getBlock().getAttr('data-selected') === 'true';
        if (isLocked === false && range.intersectsNode(this.$getBlock().getEl()) === false) {
            return { texts: [], remain: null };
        }

        const items = this.items.getBlocks();
        const texts: string[] = [];

        // 목록은 아이템 단위로만 통째로 넘길 수 있으므로(부분 아이템 추출 불가), 아래 두 분기
        // 모두 is_all 값과 무관하게 항상 item.extractSelection(true) 로 아이템 전체를 추출한다.
        if (isLocked === true) {
            for (const item of items) {
                texts.push(...(await item.extractSelection(true)).texts);
            }

            if (this.isEmpty() === true) {
                await this.getParent()?.removeBlock(this);
            }

            return { texts, remain: null };
        }

        // 아이템의 "블록 전체"(불릿 등 포함)가 선택영역과 겹치는지가 아니라, 실제로 그 아이템이
        // 텍스트를 내어줬는지(=자기 판단으로 진짜 선택이 닿았는지)로 앞/뒤를 가른다 — 그래야
        // 불릿 글자만 스치고 텍스트엔 안 닿은 경우를 아이템 스스로의 판단과 어긋나지 않게 걸러낸다.
        const after: Block[] = [];
        let phase: 'before' | 'selected' | 'after' = 'before';

        for (const item of items) {
            const extracted = await item.extractSelection(true);
            if (extracted.texts.length > 0) {
                phase = 'selected';
                texts.push(...extracted.texts);
            } else if (phase !== 'before') {
                phase = 'after';
                after.push(item);
            }
        }

        let remain: Block | null = null;
        if (after.length > 0) {
            const afterList = this.createRemainBlock();
            for (const item of after) {
                afterList.items.$getElement().getEl().appendChild(item.$getBlock().getEl());
            }
            remain = afterList;
        }

        if (this.isEmpty() === true) {
            await this.getParent()?.removeBlock(this);
        }

        return { texts, remain };
    }

    /**
     * 선택영역 추출 후 뒤에 남는 아이템들을 담을 새 목록 블록을 만든다.
     *
     * @returns 새 블록 인스턴스
     */
    private createRemainBlock(): List {
        return new List(this.editor);
    }

    /**
     * 블록이 삽입되었을 때 이벤트를 처리한다.
     */
    async onInserted(): Promise<void> {
        const range = this.editor.selection.getRange();
        if (range !== null && range.collapsed === false) {
            const container = this.getParent();
            if (container !== null) {
                const texts: string[] = [];
                let remain: Block | null = null;

                for (const sibling of container.getBlocks()) {
                    if (sibling === this) {
                        continue;
                    }

                    const extracted = await sibling.extractSelection(false);
                    texts.push(...extracted.texts);
                    if (extracted.remain !== null) {
                        remain = extracted.remain;
                    }
                }

                // 형제로부터 받은 텍스트 각각을 줄바꿈(<br>) 기준으로 나눠 아이템 하나씩 채운다.
                // 빈 줄은 아이템으로 만들지 않는다.
                const lines = texts.flatMap((text) => text.split('<br>')).filter((line) => line.trim() !== '');
                if (lines.length > 0) {
                    const [firstItem] = this.items.getBlocks() as Item[];

                    for (let index = 0; index < lines.length; index++) {
                        let item = index === 0 ? firstItem : null;
                        if (!item) {
                            item = new Item(this.editor);
                            this.items.$getElement().append(item.$getBlock());
                            await item.render();
                        }

                        await item.text.setValue(lines[index]);
                    }
                }

                if (remain !== null) {
                    await container.insertBlock(remain, this.$getBlock().getEl().nextSibling);
                }
            }
        }

        this.focus(true);
    }

    /**
     * 클립보드에서 붙여넣기 하였을 때 이벤트를 처리한다.
     *
     * @param type - 클립보드 타입
     * @param data - 클립보드 데이터
     * @param focused - Element 인 경우 나의 블록의 포커스된 엘리먼트, `Block`인 경우 새 블록을 삽입할 기준블록
     * @returns 처리결과 (true 인 경우 모든 데이터를 처리함, 그렇지 않은 경우 처리되지 않은 데이터)
     */
    async onPaste(type: string, data: any, focused: Block | Element | null = null): Promise<true | any> {
        if (type.startsWith('text/') === false) {
            return data;
        }

        if (focused === null || focused instanceof Element) {
            return data;
        }

        if (type === 'text/html') {
            const dom = document.createElement('div');
            dom.innerHTML = data as string;

            const container = focused.getParent();
            if (container === null) {
                return data;
            }

            const $matched = dom.querySelector(`rootin-block[data-type='${this.getType()}']`);
            if ($matched !== null) {
                $matched.remove();
                const remain = dom.innerHTML === '' ? true : dom.innerHTML;

                await this.setValueFromHtml($matched.outerHTML);
                await container.insertBlock(this, focused.$getBlock().getEl().nextSibling);

                return remain;
            }

            const $orphans = Array.from(dom.querySelectorAll(`rootin-block[data-type='${Item.type}']`)).filter(
                (el) => (el.parentElement?.closest('rootin-block') ?? null) === null
            );
            if ($orphans.length > 0) {
                for (const $orphan of $orphans) {
                    $orphan.remove();
                    const item = Item.create(this.editor);
                    await item.setValueFromHtml($orphan.outerHTML);
                    this.items.$getElement().append(item.$getBlock());
                    await item.render();
                }
                const remain = dom.innerHTML === '' ? true : dom.innerHTML;

                await container.insertBlock(this, focused.$getBlock().getEl().nextSibling);

                return remain;
            }
        }

        return data;
    }
}
